import React, { useState, useEffect } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { permissionService } from '../services/permissionService';
import EditUserDialog from './EditUserDialog';
import { getFetchErrorMessage } from '../services/apiService';

interface User {
  id: string;
  userName?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  emailConfirmed: boolean;
  phoneNumber?: string;
  phoneNumberConfirmed: boolean;
  twoFactorEnabled: boolean;
  lockoutEnd?: string;
  lockoutEnabled: boolean;
  accessFailedCount: number;
  createdAt?: string;
  lastLoginAt?: string;
  isActive: boolean;
  roles: string[];
}

interface UsersTableProps {
  externalAddTrigger?: number;
  hideAddButton?: boolean;
  onAddClick?: () => void;
  editPermission?: string;
  deletePermission?: string;
  refreshTrigger?: number;
}

const UsersTable: React.FC<UsersTableProps> = ({ 
  externalAddTrigger, 
  hideAddButton = false,
  onAddClick,
  editPermission,
  deletePermission,
  refreshTrigger 
}) => {
  const [, forceUpdate] = useState({});
  const canEdit = editPermission ? permissionService.hasPermission(editPermission) : true;
  const canDelete = deletePermission ? permissionService.hasPermission(deletePermission) : true;
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Subscribe to permission changes
  useEffect(() => {
    const unsubscribe = permissionService.onPermissionsChanged(() => {
      forceUpdate({});
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Refresh when external trigger changes
  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) {
      fetchUsers();
    }
  }, [refreshTrigger]);

  // Handle external add trigger
  useEffect(() => {
    if (externalAddTrigger && externalAddTrigger > 0 && onAddClick) {
      onAddClick();
    }
  }, [externalAddTrigger, onAddClick]);

  const fetchUsers = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/users`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      } else {
        const errorMessage = await getFetchErrorMessage(response, 'Nem sikerült betölteni a felhasználókat.');
        setErrorMessage(errorMessage);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Nem sikerült betölteni a felhasználókat.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('hu-HU');
  };

  const getFullName = (user: User) => {
    if (user.firstName && user.lastName) {
      return `${user.lastName} ${user.firstName}`;
    }
    return user.userName || 'N/A';
  };

  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedUser) return;

    try {
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const response = await fetch(`${apiUrl}/users/${selectedUser.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setSuccessMessage('Felhasználó sikeresen törölve');
        fetchUsers();
      } else {
        throw new Error(await getFetchErrorMessage(response, 'Hiba történt a felhasználó törlése során.'));
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setErrorMessage('Hiba történt a felhasználó törlése során');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedUser(null);
    }
  };

  const handleEditDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedUser(null);
  };

  const handleEditSuccess = () => {
    setSuccessMessage('Felhasználó sikeresen frissítve');
    fetchUsers();
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Felhasználók betöltése...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3 
      }}>
        <Typography variant="h4" gutterBottom>
          Felhasználók kezelése
        </Typography>
        {!hideAddButton && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAddClick}
            sx={{ backgroundColor: '#ff8c42', '&:hover': { backgroundColor: '#e67a35' } }}
          >
            Új felhasználó
          </Button>
        )}
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Név</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Felhasználónév</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Szerepkörök</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Létrehozva</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Állapot</TableCell>
              <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Műveletek</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id} hover>
                <TableCell sx={{ fontWeight: 'medium' }}>
                  {getFullName(user)}
                </TableCell>
                <TableCell>
                  {user.userName || '-'}
                </TableCell>
                <TableCell>
                  {user.email || '-'}
                  {user.emailConfirmed && (
                    <Chip
                      label="Megerősített"
                      size="small"
                      color="success"
                      sx={{ ml: 1 }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {user.roles.map((role, index) => (
                      <Chip
                        key={index}
                        label={role}
                        size="small"
                        color="primary"
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  {formatDate(user.createdAt)}
                </TableCell>
                <TableCell>
                  <Chip
                    label={user.isActive ? 'Aktív' : 'Inaktív'}
                    color={user.isActive ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ textAlign: 'center' }}>
                  {canEdit && (
                    <Tooltip title="Szerkesztés">
                      <IconButton
                        size="small"
                        sx={{ color: '#ff8c42', mr: 1 }}
                        onClick={() => handleEditClick(user)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {canDelete && (
                    <Tooltip title="Törlés">
                      <IconButton
                        size="small"
                        sx={{ color: '#f44336' }}
                        onClick={() => handleDeleteClick(user)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {users.length === 0 && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h6" color="textSecondary">
            Nincsenek felhasználók
          </Typography>
        </Box>
      )}

      {/* Edit User Dialog */}
      <EditUserDialog
        open={editDialogOpen}
        user={selectedUser}
        onClose={handleEditDialogClose}
        onSuccess={handleEditSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Felhasználó törlése</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Biztosan törölni szeretnéd a(z) <strong>{selectedUser?.userName}</strong> felhasználót?
            Ez a művelet nem vonható vissza.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Mégse
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Törlés
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Messages */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UsersTable;
